import type { ReactNode } from 'react';
import { ApiOutlined, ReloadOutlined, ShopOutlined } from '@ant-design/icons';

import { Button, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { maLoi } from '../../../../lib/errors';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';

/**
 * Bốn trạng thái của một vùng dữ liệu (CLAUDE.md), dùng chung cho mười hai màn của Trợ lý AI.
 *
 * Hai trạng thái lỗi được nói riêng, vì bấm Thử lại ở đó không bao giờ khác đi:
 * - `TENANT_DOMAIN_NOT_VERIFIED`: nhân sự nền tảng xem chéo giao diện tenant (ADR-UI-0012 mục 9) — họ không thuộc tenant
 *   nào, nên Gateway từ chối ở bước 3.
 * - `API_UNAVAILABLE`: module chưa chạy trên máy chủ này (Gateway trả 404/502 của chính nó).
 */
export interface VungDuLieuProps<T> {
  dangTai: boolean;
  loi: unknown;
  dulieu: T | undefined;
  /** Rỗng khi `dulieu` có nhưng không có hàng nào. */
  rong: boolean;
  /** Tiêu đề trạng thái rỗng; bỏ `rongMo` và `rongHanhDong` khi rỗng *vì bộ lọc*. */
  rongTieuDe: string;
  rongMo?: string;
  rongHanhDong?: ReactNode;
  rongIcon?: Parameters<typeof Empty>[0]['icon'];
  onThuLai: () => void;
  /** Khung chờ riêng (lưới thẻ, bảng); bỏ trống thì ba thanh xương. */
  khungCho?: ReactNode;
  children: (du: T) => ReactNode;
}

export function VungDuLieu<T>({
  dangTai, loi, dulieu, rong, rongTieuDe, rongMo, rongHanhDong, rongIcon, onThuLai, khungCho, children,
}: VungDuLieuProps<T>) {
  const { t } = useT();
  const moTaLoi = useMoTaLoi();

  if (dangTai) {
    return (
      khungCho ?? (
        <div className="space-y-2 p-4" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      )
    );
  }

  if (loi) return <KhoiLoi loi={loi} onThuLai={onThuLai} moTa={moTaLoi(loi)} t={t} />;

  if (!dulieu || rong) {
    return <Empty icon={rongIcon} title={rongTieuDe} description={rongMo} action={rongHanhDong} />;
  }

  return <>{children(dulieu)}</>;
}

/** Dải lỗi đứng riêng — cho chỗ cần báo lỗi mà không bọc cả vùng (hộp thoại, khối phụ). */
export function DaiLoi({ loi, onThuLai }: { loi: unknown; onThuLai?: () => void }) {
  const { t } = useT();
  const moTaLoi = useMoTaLoi();
  return <KhoiLoi loi={loi} onThuLai={onThuLai} moTa={moTaLoi(loi)} t={t} />;
}

function KhoiLoi({
  loi, onThuLai, moTa, t,
}: {
  loi: unknown;
  onThuLai?: () => void;
  moTa: string;
  t: ReturnType<typeof useT>['t'];
}) {
  const ma = maLoi(loi);
  if (ma === 'TENANT_DOMAIN_NOT_VERIFIED') {
    return <Empty icon={ShopOutlined} title={t('troLyAi.xemCheoTieuDe')} description={t('troLyAi.xemCheoMo')} />;
  }
  if (ma === 'API_UNAVAILABLE') {
    return (
      <Empty
        icon={ApiOutlined}
        title={t('troLyAi.chuaSanSangTieuDe')}
        description={t('troLyAi.chuaSanSangMo')}
        action={
          onThuLai ? (
            <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={onThuLai}>
              {t('chung.thuLai')}
            </Button>
          ) : undefined
        }
      />
    );
  }
  return (
    <div className="p-4" role="alert">
      <div className="rounded-lg border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
        <p className="break-words">{moTa}</p>
        {onThuLai && (
          <Button variant="outline" size="sm" className="mt-3" icon={<ReloadOutlined />} onClick={onThuLai}>
            {t('chung.thuLai')}
          </Button>
        )}
      </div>
    </div>
  );
}
